import React from 'react';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';

/**
 * Theme icons for each mode
 */
interface ThemeIconProps {
  theme: string;
  size?: number;
}

const ThemeIcon = ({ theme, size = 20 }: ThemeIconProps) => {
  const iconStyle = { width: size, height: size };

  switch (theme) {
    case THEMES.DARK:
      // Moon icon for dark (black background)
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
      );
    case THEMES.LIGHT:
      // Sun icon for light (gray background)
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
        </svg>
      );
    default:
      return null;
  }
};

/**
 * Simple theme toggle button
 */
export function ThemeSwitcher() {
  const { theme, cycleTheme } = useTheme();
  const { t } = useTranslation();

  const getLabel = () => {
    switch (theme) {
      case THEMES.DARK:
        return t('themeDark');
      case THEMES.LIGHT:
        return t('themeLight');
      default:
        return '';
    }
  };

  return (
    <button
      onClick={cycleTheme}
      className="theme-toggle-btn nav-link"
      title={getLabel()}
      aria-label={getLabel()}
      style={{
        background: 'transparent',
        border: 'none',
        color: 'var(--navbar-text)',
        cursor: 'pointer',
        padding: '0.5rem',
        borderRadius: '0.375rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.2s ease',
      }}
    >
      <ThemeIcon theme={theme} />
    </button>
  );
}

export default ThemeSwitcher;
